import styled from 'styled-components';
import { BasePanel } from 'styles/components';
import {
  Colors,
  ColorScheme,
  Fonts,
  getScrollbarStyle,
} from 'styles/constants';

export const Page = styled.div`
  width: 100%;
  max-width: 1800px;
  margin: 0 auto;
  padding: 24px;
  color: ${ColorScheme.Text};

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

export const MainPanel = styled(BasePanel)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
`;

export const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

// Rastreios à esquerda, mapa no centro e o passo atual à direita.
export const EditorGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 280px) minmax(0, 1fr) minmax(
      300px,
      380px
    );
  grid-template-areas: 'routes map side';
  gap: 16px;
  /* A tela cabe na janela: o mapa e os botões ficam sempre à vista e só as colunas
     laterais rolam. Sem sticky, porque o Scaffold usa overflow-x: hidden. */
  height: max(640px, calc(100vh - 200px));

  @media (max-width: 1279px) {
    grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
    grid-template-areas:
      'routes map'
      'side side';
    height: auto;
  }

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'routes'
      'map'
      'side';
  }
`;

// O painel da lista ocupa a coluna inteira; quem rola é só a lista de rastreios.
export const RoutesColumn = styled.div`
  grid-area: routes;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;

  & > * {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
  }
`;

export const RouteList = styled.ol`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  overflow-y: auto;
  padding: 0 12px 12px;
  ${getScrollbarStyle()};

  @media (max-width: 960px) {
    max-height: 260px;
  }
`;

export const SideColumn = styled.div`
  grid-area: side;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  ${getScrollbarStyle()};

  /* Os painéis mantêm a altura do conteúdo; quem rola é a coluna. */
  & > * {
    flex-shrink: 0;
  }
`;

export const MapColumn = styled.section`
  grid-area: map;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  min-height: 0;
`;

export const Panel = styled.section`
  min-width: 0;
  overflow: hidden;
  border: 1px solid ${Colors.Gray30};
  border-radius: 8px;
  background: ${Colors.White};
`;

export const PanelHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid ${Colors.Gray30};
`;

export const RouteItem = styled.li<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 8px;
  border: 1px solid
    ${({ $active }) => ($active ? ColorScheme.Primary : Colors.Gray30)};
  border-radius: 6px;
  background: ${({ $active }) => ($active ? Colors.Peach : Colors.White)};
`;

const BaseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border-radius: 4px;
  font-family: ${Fonts.GilroyBold};
  font-size: 14px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

export const PrimaryButton = styled(BaseButton)`
  border: 1px solid ${ColorScheme.Primary};
  background: ${ColorScheme.Primary};
  color: ${Colors.White};

  &:hover:not(:disabled) {
    filter: brightness(0.92);
  }
`;

export const SecondaryButton = styled(BaseButton)`
  border: 1px solid ${Colors.Gray40};
  background: ${Colors.White};
  color: ${Colors.Gray60};

  &:hover:not(:disabled) {
    background: ${Colors.Gray10};
  }
`;

export const OutlineButton = styled(BaseButton)`
  padding: 0 12px;
  border: 1px solid ${ColorScheme.Primary};
  background: ${Colors.White};
  color: ${ColorScheme.Primary};

  &:hover:not(:disabled) {
    background: ${Colors.Peach};
  }
`;

export const SegmentButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 36px;
  border-radius: 4px;
  font-family: ${Fonts.GilroyBold};
  font-size: 12px;
  background: ${({ $active }) => ($active ? ColorScheme.Primary : 'transparent')};
  color: ${({ $active }) => ($active ? Colors.White : Colors.Gray60)};

  &:hover {
    background: ${({ $active }) => ($active ? ColorScheme.Primary : Colors.White)};
  }
`;

export const NameInput = styled.input<{ $invalid: boolean }>`
  height: 40px;
  padding: 0 12px;
  border: 1px solid
    ${({ $invalid }) => ($invalid ? ColorScheme.Danger : Colors.Gray40)};
  border-radius: 4px;
  font-family: ${Fonts.OpenSans};
  font-weight: 400;
  outline: none;

  &:focus {
    border-color: ${({ $invalid }) => ($invalid ? ColorScheme.Danger : ColorScheme.Primary)};
  }
`;

// O status vai escrito por extenso; a cor só reforça.
export const StatusBadge = styled.span<{ $status: 'draft' | 'active' }>`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border: 1px solid
    ${({ $status }) => ($status === 'active' ? Colors.Green : Colors.Gray40)};
  border-radius: 999px;
  font-family: ${Fonts.GilroyBold};
  font-size: 11px;
  color: ${({ $status }) => ($status === 'active' ? Colors.Gray70 : Colors.Gray60)};
  background: ${({ $status }) => ($status === 'active' ? Colors.White : Colors.Gray10)};
`;

// `modal-content` escurece o fundo, como nas outras janelas do projeto.
export const WarehouseDialog = styled.div.attrs({ className: 'modal-content' })`
  display: flex;
  flex-direction: column;
  width: min(640px, calc(100vw - 32px));
  max-height: calc(100vh - 64px);
  border-radius: 8px;
  background: ${Colors.White};
  color: ${ColorScheme.Text};
`;

// Campo de busca com a lupa à esquerda, sem cobrir o texto.
export const SearchInput = styled(NameInput)`
  width: 100%;
  padding-left: 32px;
`;

// Topo do rastreio aberto: nome e descrição editáveis no lugar, status e o que falta.
export const RouteHeader = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px 24px;
  padding: 12px 16px;
  border: 1px solid ${Colors.Gray30};
  border-radius: 8px;
  background: ${Colors.White};
`;

// Do tamanho do texto, para o lápis e o status ficarem ao lado do nome.
export const TitleInput = styled.input<{ $invalid: boolean }>`
  field-sizing: content;
  min-width: 12ch;
  max-width: 100%;
  padding: 2px 6px;
  margin-left: -6px;
  border: 1px solid
    ${({ $invalid }) => ($invalid ? ColorScheme.Danger : 'transparent')};
  border-radius: 4px;
  font-family: ${Fonts.GilroyBold};
  font-size: 20px;
  color: ${ColorScheme.Text};
  background: transparent;
  outline: none;

  &:hover:not(:disabled) {
    border-color: ${Colors.Gray40};
  }

  &:focus {
    border-color: ${({ $invalid }) => ($invalid ? ColorScheme.Danger : ColorScheme.Primary)};
    background: ${Colors.White};
  }
`;

export const InlineDescriptionInput = styled.input`
  width: 100%;
  padding: 2px 6px;
  margin-left: -6px;
  border: 1px solid transparent;
  border-radius: 4px;
  font-family: ${Fonts.OpenSans};
  font-size: 13px;
  color: ${Colors.Gray60};
  background: transparent;
  outline: none;

  &:hover:not(:disabled) {
    border-color: ${Colors.Gray40};
  }

  &:focus {
    border-color: ${ColorScheme.Primary};
    background: ${Colors.White};
  }
`;

// Cartão que flutua sobre o canto inferior esquerdo do mapa (a legenda dos anéis).
export const MapOverlayCard = styled.section`
  position: absolute;
  bottom: 24px;
  left: 12px;
  z-index: 2;
  width: min(240px, calc(100% - 24px));
  max-height: calc(100% - 80px);
  overflow-y: auto;
  border: 1px solid ${Colors.Gray30};
  border-radius: 8px;
  background: ${Colors.White};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  ${getScrollbarStyle()};
`;

// Amostra da linha do anel na legenda: o de chegada cheio em laranja, os outros tracejados.
export const RingSwatch = styled.span<{ $arrival: boolean }>`
  width: 18px;
  height: 0;
  border-top: 2px ${({ $arrival }) => ($arrival ? 'solid' : 'dashed')}
    ${({ $arrival }) => ($arrival ? Colors.Orange : Colors.Gray60)};
`;

export const StepList = styled.ol`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const StepConnector = styled.span<{ $done: boolean }>`
  flex: 0 1 48px;
  height: 2px;
  background: ${({ $done }) => ($done ? Colors.Green : Colors.Gray40)};

  @media (max-width: 767px) {
    display: none;
  }
`;

export const StepButton = styled.button<{ $current: boolean }>`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid
    ${({ $current }) => ($current ? ColorScheme.Primary : Colors.Gray30)};
  border-radius: 8px;
  background: ${({ $current }) => ($current ? Colors.Peach : Colors.White)};
  font-family: ${Fonts.GilroyBold};
  font-size: 14px;
  color: ${ColorScheme.Text};

  &:hover {
    border-color: ${ColorScheme.Primary};
  }
`;

export const StepNumber = styled.span<{ $current: boolean; $done: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  font-family: ${Fonts.GilroyBold};
  font-size: 13px;
  color: ${({ $current, $done }) => ($current || $done ? Colors.White : Colors.Gray60)};
  background: ${({ $current, $done }) =>
    $done ? Colors.Green : $current ? ColorScheme.Primary : Colors.Gray30};
`;
